import React, { useEffect, useState } from "react";
import { Button, Modal, Tabs } from "@opendate-app/setlist";
import { useBroadcastEditContext } from "./BroadcastEditContainer";
import BroadcastSidebar, { StepFooter } from "./BroadcastSidebar";
import TemplateHTMLPreview from "../BeefreeTemplates/TemplateHTMLPreview";
import { templateSelectionParams } from "./broadcastTemplates";

const templateTabs = [
  { value: "Opendate Blueprints", label: "Opendate Blueprints" },
  { value: "Team Blueprints", label: "Team Blueprints" },
  { value: "Previous Broadcasts", label: "Previous Broadcasts" },
];

const styles = {
  heading: {
    color: "var(--sl-text-primary)",
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 4,
  },
  description: {
    color: "var(--sl-text-tertiary)",
  },
  grid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 16,
    marginTop: 24,
  },
  card: {
    width: 208,
    border: "1px solid var(--sl-border-secondary)",
    borderRadius: 12,
    background: "var(--sl-bg-primary)",
    boxShadow: "var(--sl-shadow-xs)",
    overflow: "hidden",
  },
  thumbnail: {
    position: "relative",
    height: 168,
    margin: 16,
    border: "1px solid var(--sl-border-secondary)",
    overflow: "hidden",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  thumbnailFrame: {
    width: 560,
    height: 560,
    overflow: "hidden",
    border: "none",
    transform: "scale(0.315)",
    transformOrigin: "0 0",
    position: "absolute",
    top: 0,
    left: 0,
    pointerEvents: "none",
  },
  details: {
    borderTop: "1px solid var(--sl-border-secondary)",
    padding: 16,
  },
  name: {
    color: "var(--sl-text-primary)",
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  status: {
    color: "var(--sl-text-tertiary)",
    fontSize: 14,
    marginTop: 8,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 12,
  },
  empty: {
    color: "var(--sl-text-tertiary)",
  },
  tabs: {
    marginTop: 24,
  },
  preview: {
    height: "min(600px, 70vh)",
  },
  previewFrame: {
    width: "100%",
    height: "100%",
    overflow: "hidden",
    border: "none",
  },
};

const ContentTemplate = ({
  current = false,
  name,
  img,
  previewable = true,
  comingSoon = false,
  template = null,
  isBlueprint = false,
  isTeamBlueprint = false,
}) => {
  const {
    team,
    broadcast,
    updateBroadcast,
    setPreviewTemplate,
  } = useBroadcastEditContext();

  const handleSelect = () => {
    const params = templateSelectionParams(broadcast, template, { isBlueprint, isTeamBlueprint });

    updateBroadcast(params, false, () => {
      window.location.href = `/teams/${team.id}/broadcasts/${broadcast.id}/edit?tab=content`
    });
  };

  return (
    <div style={styles.card}>
      <div style={styles.thumbnail}>
        {img ? (
          <img src={img} alt={name} style={styles.image} />
        ) : (
          <TemplateHTMLPreview htmlContent={template.template_html} style={styles.thumbnailFrame} />
        )}
      </div>
      <div style={styles.details}>
        <div style={styles.name} title={name}>{current ? "Current" : name}</div>
        {comingSoon ? (
          <div style={styles.status}>Coming soon!</div>
        ) : (
          <div style={styles.actions}>
            {current ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  window.location.href = `/teams/${team.id}/broadcasts/${broadcast.id}/edit?tab=content`
                }}
              >
                Edit
              </Button>
            ) : (
              <Button variant="primary" size="sm" aria-label={name ? `Select ${name}` : undefined} onClick={handleSelect}>
                Select
              </Button>
            )}
            {previewable ? (
              <Button
                variant="link-color"
                size="sm"
                aria-label={name ? `Preview ${name}` : undefined}
                onClick={() => {
                  setPreviewTemplate((isBlueprint || isTeamBlueprint) ? { ...template, templatable: { name: template.name } } : template);
                }}
              >
                Preview
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

const ContentSelectTab = () => {
  const {
    setTab,
    broadcast,
    previousBroadcastTemplates,
    fetchPreviousBroadcastTemplates,
    adminBlueprints,
    fetchAdminBlueprints,
    teamBlueprints,
    fetchTeamBlueprints,
    previewTemplate,
    setPreviewTemplate,
  } = useBroadcastEditContext();

  const [templateTab, setTemplateTab] = useState("Opendate Blueprints");

  useEffect(() => {
    fetchPreviousBroadcastTemplates();
    fetchAdminBlueprints();
    fetchTeamBlueprints();
  }, []);

  return (
    <BroadcastSidebar>
      <h3 style={styles.heading}>Content</h3>
      <p style={styles.description}>Design the content of your email.</p>
      {broadcast.beefree_template.template ? (
        <div style={styles.grid}>
          <ContentTemplate
            current={true}
            previewable={false}
            key={broadcast.beefree_template.id}
            name={broadcast.name}
            template={broadcast.beefree_template}
          />
        </div>
      ) : null}
      <div style={styles.tabs}>
        <Tabs items={templateTabs} value={templateTab} onChange={setTemplateTab} />
      </div>
      <div style={styles.grid}>
        {templateTab === "Opendate Blueprints" ? (
          <>
            <ContentTemplate name="Start from Scratch" img="/scratch.jpg" previewable={false} />
            {adminBlueprints.map(blueprint => (
              <ContentTemplate
                key={blueprint.id}
                name={blueprint.name}
                template={blueprint}
                isBlueprint={true}
              />
            ))}
          </>
        ) : templateTab === "Team Blueprints" ? (
          teamBlueprints.length > 0 ? (
            teamBlueprints.map(blueprint => (
              <ContentTemplate
                key={blueprint.id}
                name={blueprint.name}
                template={blueprint}
                isTeamBlueprint={true}
              />
            ))
          ) : (
            <p style={styles.empty}>No team blueprints yet. Save a template as a blueprint to get started.</p>
          )
        ) : templateTab === "Previous Broadcasts" ? (
          previousBroadcastTemplates
            .filter(template => template.templatable.id !== broadcast.id)
            .map(template => (
              <ContentTemplate
                key={template.id}
                name={template.templatable.name}
                template={template}
              />
            ))
        ) : null}
      </div>
      <StepFooter onBack={() => setTab("from")} onNext={() => setTab("review-and-schedule")} />
      <Modal
        open={previewTemplate !== null}
        onClose={() => setPreviewTemplate(null)}
        width={840}
        title={previewTemplate?.templatable.name}
      >
        {previewTemplate ? (
          <div style={styles.preview}>
            <TemplateHTMLPreview htmlContent={previewTemplate.template_html} style={styles.previewFrame} />
          </div>
        ) : null}
      </Modal>
    </BroadcastSidebar>
  );
};

export default ContentSelectTab;
